import { AUDIO_CODECS, SAMPLE_RATES } from './audio.js'
import { ERROR_CODES } from './codes.js'
import type { Infer } from './shape.js'
import {
  array,
  boolean,
  integer,
  literal,
  object,
  oneOf,
  oneOfNumber,
  optional,
  string,
  variant,
} from './shape.js'

/**
 * Everything the two ends say to each other, in one file.
 *
 * Control travels as JSON text frames and audio travels as binary frames, so
 * there is never a question about which a frame is: the WebSocket already
 * tells you. What follows defines only the text side.
 *
 * The shape of the conversation is: the client opens with `hello`, the server
 * answers `ready` or `error`, and after that both sides talk freely until one
 * of them closes. Nothing else is ordered, because ordering assumptions are
 * what break when a phone changes network mid-sentence.
 */

export const PROTOCOL_VERSION = 1

/** Locales the server will accept. Kept in step with the app's own list. */
export const LOCALES = ['en', 'ha'] as const
export type Locale = (typeof LOCALES)[number]

/**
 * What the assistant is doing, as far as the client needs to know.
 *
 * Deliberately coarser than the client's own state machine, which also tracks
 * connecting, permission prompts and holds. Those are local facts, and a
 * server that tried to own them would be describing a screen it cannot see.
 */
export const TURN_STATES = ['idle', 'listening', 'thinking', 'speaking'] as const
export type TurnState = (typeof TURN_STATES)[number]

const format = object({
  codec: oneOf(AUDIO_CODECS),
  sampleRate: oneOfNumber(SAMPLE_RATES),
})

/**
 * What the client knows about the person, sent up so the model can use it.
 *
 * The profile lives on the device and is pushed into each session rather than
 * being read from the server's own tables, so a person whose device holds a
 * name the server never stored is still greeted by it. Every field is optional
 * because every field is genuinely optional now that nothing interviews them.
 */
const profileContext = object({
  name: optional(string({ max: 80 })),
  region: optional(string({ max: 120 })),
  ageBand: optional(string({ max: 16 })),
  gender: optional(string({ max: 24 })),
  interests: optional(array(string({ max: 64 }), { max: 24 })),
})

export type ProfileContext = Infer<typeof profileContext>

/* ------------------------------------------------------------------ */
/* Client to server                                                    */
/* ------------------------------------------------------------------ */

/**
 * The opening frame, and the only one allowed before the server answers.
 *
 * The token is in the body rather than in a header or a query string. Browsers
 * cannot set headers on a WebSocket handshake, and a query string ends up in
 * access logs and proxy logs on the way, which is the one place a credential
 * should never be.
 */
const hello = object({
  type: literal('hello'),
  protocol: integer({ min: 1, max: 1000 }),
  token: string({ min: 1, max: 4096 }),
  locale: oneOf(LOCALES),
  /** What the client will send. */
  input: format,
  /** What the client can play. The server picks within this. */
  output: format,
  profile: optional(profileContext),
  /**
   * Whether the assistant should speak first.
   *
   * True for a fresh conversation, because somebody who has just tapped a
   * button and put a phone to their ear needs to hear that it is listening.
   * False on a resume, where the conversation is already underway and an
   * unprompted greeting would be the assistant interrupting itself.
   */
  greet: optional(boolean()),
  resume: optional(
    object({
      sessionId: string({ min: 1, max: 64 }),
      /** The last server event the client actually saw. */
      lastEventId: integer({ min: 0 }),
    }),
  ),
  client: optional(
    object({
      name: string({ max: 64 }),
      version: string({ max: 32 }),
    }),
  ),
})

/** The person started talking. Sent by the client's own voice detection. */
const startTurn = object({ type: literal('start_turn') })

/**
 * The person stopped talking, so the audio sent so far is a complete turn.
 *
 * Advisory. A model that does its own endpointing may have decided the turn
 * was over already, and a model that does not needs to be told. Either way the
 * client says what it observed and the server reconciles it.
 */
const endTurn = object({ type: literal('end_turn') })

/**
 * Stop talking, the person is talking over you.
 *
 * Separate from `end_turn` because they are opposite requests. One says the
 * input is complete; this one says the output is unwanted. Conflating them was
 * the single most common source of an assistant that talks over people.
 */
const interrupt = object({ type: literal('interrupt') })

const setMuted = object({ type: literal('mute'), muted: boolean() })

/** Suspends the whole exchange. Distinct from mute, which only closes the mic. */
const setHeld = object({ type: literal('hold'), held: boolean() })

/** Updated profile mid-session, for example after the person changes a name. */
const context = object({ type: literal('context'), profile: profileContext })

const bye = object({
  type: literal('bye'),
  reason: optional(string({ max: 120 })),
})

/**
 * Application level keepalive.
 *
 * WebSocket has ping and pong control frames already, but a browser cannot
 * send or observe them: the API gives a page no access to either. So the
 * client needs its own, and the server answers both kinds.
 */
const ping = object({ type: literal('ping'), at: integer({ min: 0 }) })

export const clientMessage = variant('type', {
  hello,
  start_turn: startTurn,
  end_turn: endTurn,
  interrupt,
  mute: setMuted,
  hold: setHeld,
  context,
  bye,
  ping,
})

export type ClientMessage = Infer<typeof clientMessage>
export type HelloMessage = Infer<typeof hello>

/** Message types that are legal before the handshake completes. */
export const PRE_HANDSHAKE_TYPES: readonly ClientMessage['type'][] = ['hello']

/* ------------------------------------------------------------------ */
/* Server to client                                                    */
/* ------------------------------------------------------------------ */

/**
 * Limits the server is actually enforcing, told to the client up front.
 *
 * A client that knows the ceiling can warn somebody before it hits, which is
 * the difference between a conversation that ends and a conversation that gets
 * cut off. These are facts about this session, not the plan's marketing copy.
 */
export interface SessionLimits {
  /** Wall clock ceiling for one session, in seconds. */
  maxSessionSeconds: number
  /** How long a single spoken turn may run, in seconds. */
  maxTurnSeconds: number
  /** How long the server will hold a dropped session open for a resume. */
  resumeWindowSeconds: number
  /** How often the client should ping when nothing else is happening. */
  keepaliveSeconds: number
}

export interface ReadyMessage {
  type: 'ready'
  id: number
  sessionId: string
  protocol: number
  /** True when this attached to an existing session instead of starting one. */
  resumed: boolean
  /** The format the server will send audio in. Within what the client offered. */
  output: { codec: string; sampleRate: number }
  limits: SessionLimits
  model: {
    id: string
    /** False when replies come from the local stand-in rather than a model. */
    backed: boolean
  }
}

export interface StateMessage {
  type: 'state'
  id: number
  state: TurnState
  turn: number
}

export interface TranscriptMessage {
  type: 'transcript'
  id: number
  turn: number
  role: 'user' | 'assistant'
  text: string
  /**
   * False while the words are still changing. A partial replaces the previous
   * partial for the same turn; a final one closes it.
   */
  final: boolean
}

/** Audio for this turn starts now. Binary frames follow until `speech_end`. */
export interface SpeechBeginMessage {
  type: 'speech_begin'
  id: number
  turn: number
}

export interface SpeechEndMessage {
  type: 'speech_end'
  id: number
  turn: number
  /** True when the turn was cut short by a barge-in rather than finishing. */
  interrupted: boolean
}

/**
 * The model asked to remember something about the person.
 *
 * The server does not apply it. The profile lives on the device, so this is a
 * request the client decides about, which keeps "what Namu knows" answerable
 * by looking at one place.
 */
export interface ProfilePatchMessage {
  type: 'patch'
  id: number
  profile: ProfileContext
}

export interface ErrorMessage {
  type: 'error'
  id: number
  code: (typeof ERROR_CODES)[number]
  message: string
  /** Present when the failure belongs to one turn rather than the session. */
  turn?: number
}

/** Sent immediately before the server closes, so the reason survives the close. */
export interface ClosingMessage {
  type: 'closing'
  id: number
  code: (typeof ERROR_CODES)[number] | 'complete'
  message: string
}

export interface PongMessage {
  type: 'pong'
  id: number
  /** Echoed back from the ping, so the client can measure the round trip. */
  at: number
  /** The server's clock, for a rough offset. */
  serverAt: number
}

export type ServerMessage =
  | ReadyMessage
  | StateMessage
  | TranscriptMessage
  | SpeechBeginMessage
  | SpeechEndMessage
  | ProfilePatchMessage
  | ErrorMessage
  | ClosingMessage
  | PongMessage

/**
 * The client's validator for what the server sends.
 *
 * Lighter than the server's validator for client messages, and deliberately
 * so. The threat models are not symmetric: the server is parsing input from
 * anyone on the internet, while the client is parsing its own server, where a
 * malformed message is a bug to be surfaced rather than an attack to be
 * repelled. What this does buy is that a client on an older build fails on one
 * unrecognised message instead of throwing somewhere further in.
 */
export const serverMessage = variant('type', {
  ready: object({
    type: literal('ready'),
    id: integer({ min: 0 }),
    sessionId: string({ max: 64 }),
    protocol: integer({ min: 1 }),
    resumed: boolean(),
    output: object({ codec: string({ max: 16 }), sampleRate: integer({ min: 1 }) }),
    limits: object({
      maxSessionSeconds: integer({ min: 1 }),
      maxTurnSeconds: integer({ min: 1 }),
      resumeWindowSeconds: integer({ min: 0 }),
      keepaliveSeconds: integer({ min: 1 }),
    }),
    model: object({ id: string({ max: 64 }), backed: boolean() }),
  }),
  state: object({
    type: literal('state'),
    id: integer({ min: 0 }),
    state: oneOf(TURN_STATES),
    turn: integer({ min: 0 }),
  }),
  transcript: object({
    type: literal('transcript'),
    id: integer({ min: 0 }),
    turn: integer({ min: 0 }),
    role: oneOf(['user', 'assistant'] as const),
    text: string({ max: 16384 }),
    final: boolean(),
  }),
  speech_begin: object({
    type: literal('speech_begin'),
    id: integer({ min: 0 }),
    turn: integer({ min: 0 }),
  }),
  speech_end: object({
    type: literal('speech_end'),
    id: integer({ min: 0 }),
    turn: integer({ min: 0 }),
    interrupted: boolean(),
  }),
  patch: object({
    type: literal('patch'),
    id: integer({ min: 0 }),
    profile: profileContext,
  }),
  error: object({
    type: literal('error'),
    id: integer({ min: 0 }),
    code: oneOf(ERROR_CODES),
    message: string({ max: 1024 }),
    turn: optional(integer({ min: 0 })),
  }),
  closing: object({
    type: literal('closing'),
    id: integer({ min: 0 }),
    code: oneOf([...ERROR_CODES, 'complete'] as const),
    message: string({ max: 1024 }),
  }),
  pong: object({
    type: literal('pong'),
    id: integer({ min: 0 }),
    at: integer({ min: 0 }),
    serverAt: integer({ min: 0 }),
  }),
})
